import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Row, Col, Label, Form } from 'reactstrap'
import Resx from '@/components/Resx'
import Icon from '@/components/Icon'
import CheckBox from '@/components/CheckBox'
import { Button } from '@/components/button'
import { Columns } from '@/components/columns'
import { WhiteBox } from '@/components/Layout'
import { TabContent, TabNavItems, TabPane } from '@/components/tabs'
import { useLocalization } from '@/hooks/localization'
import isEqual from 'lodash/isEqual'
import { DemographicsDynamicPermissions } from './demographicsPermissions'
import { InputReportPermissions } from './inputReportPermissions'
import { OutputReportDefinitionPermissions } from '@/internal-app/pages/jobPositionPermissions/outputReportDefinitionPermissions'
import { institutionPermissionTypes } from '@/enums/institutionPermissionTypes'

const JobPositionPermissionsForm = React.memo(function JobPositionPermissionsForm ({ jobAssignmentId, jobPositionPermissions, onSave }) {
  const { localizeValue } = useLocalization()
  const [granted, setGranted] = useState(jobPositionPermissions.grantedPermissions || [])
  const [disabledPermissions, setDisabledPermissions] = useState(jobPositionPermissions.disabledPermissions || [])
  const [institutionPermissions, setInstitutionPermissions] = useState(jobPositionPermissions.institutionPermissions || [])
  const [reportGroupPermissions, setReportGroupPermissions] = useState(jobPositionPermissions.reportGroupPermissions || [])
  const [outputReportDefinitionPermissions, setOutputReportDefinitionPermissions] = useState(jobPositionPermissions.outputReportDefinitionPermissions || [])
  const [pendingChanges, setPendingChanges] = useState(false)

  const institutionEdit = useMemo(() => (institutionPermissions.find(x => x.isCbiPermission) || { institutionEdit: 0 }).institutionEdit, [institutionPermissions])

  useEffect(() => {
    const validatePermissions = (hasNecessaryPermissions, canCreate, canView) => {
      if (granted.includes(canCreate) && (!hasNecessaryPermissions || !granted.includes(canView))) {
        setGranted(granted.filter(x => x !== canCreate))
      }

      if (hasNecessaryPermissions && granted.includes(canView) && disabledPermissions.includes(canCreate)) {
        setDisabledPermissions(disabledPermissions.filter(x => x !== canCreate))
      } else if (!disabledPermissions.includes(canCreate) && (!hasNecessaryPermissions || !granted.includes(canView))) {
        setDisabledPermissions([...disabledPermissions, canCreate])
      }
    }

    // Can create banks
    const hasNecessaryBankPermissions = (institutionEdit & institutionPermissionTypes.enum.BankDetail) &&
      (institutionEdit & institutionPermissionTypes.enum.BankStatus)
    validatePermissions(hasNecessaryBankPermissions, 'CanCreateBanks', 'CanViewBanks')

    // Can create companies
    const hasNecessaryCompanyPermissions = (institutionEdit & institutionPermissionTypes.enum.CompanyDetail) &&
      (institutionEdit & institutionPermissionTypes.enum.CompanyStatus) &&
      (institutionEdit & institutionPermissionTypes.enum.CompanyType)
    validatePermissions(hasNecessaryCompanyPermissions, 'CanCreateCompanies', 'CanViewCompanies')

    // Can create individuals
    validatePermissions(true, 'CanCreateIndividuals', 'CanViewIndividuals')
  }, [institutionEdit, granted, disabledPermissions])

  useEffect(() => {
    setPendingChanges(
      !isEqual(granted, jobPositionPermissions.grantedPermissions) ||
      !isEqual(institutionPermissions, jobPositionPermissions.institutionPermissions) ||
      !isEqual(reportGroupPermissions, jobPositionPermissions.reportGroupPermissions) ||
      !isEqual(outputReportDefinitionPermissions, jobPositionPermissions.outputReportDefinitionPermissions)
    )
  }, [granted, institutionPermissions, reportGroupPermissions, outputReportDefinitionPermissions,
    jobPositionPermissions.grantedPermissions,
    jobPositionPermissions.institutionPermissions,
    jobPositionPermissions.reportGroupPermissions,
    jobPositionPermissions.outputReportDefinitionPermissions])

  const [activeTab, setActiveTab] = useState('1')
  const handleChange = useCallback((perm, checked) => {
    if (checked === true) {
      setGranted([...granted, perm])
    } else {
      setGranted(granted.filter(x => x !== perm))
    }
  }, [granted])
  const handleSave = useCallback(() => onSave({ jobAssignmentId, permissions: granted, institutionPermissions, reportGroupPermissions, outputReportDefinitionPermissions }), [institutionPermissions, jobAssignmentId, onSave, granted, reportGroupPermissions, outputReportDefinitionPermissions])

  function localizedName (data, prefix) {
    const name = data[`${prefix}Name`]
    const latinName = data[`${prefix}LatinName`]
    return localizeValue(name, latinName)
  }

  function renderGroups (filter, xl = 4, extraContent = []) {
    return (
      <Row>
        <Columns xl={xl} lg={6} md={12}>
          {[
            ...jobPositionPermissions.allPermissions.filter(filter)
              .map(group => <PermissionGroup key={group.key} group={group} granted={granted} onChange={handleChange} />),
            ...extraContent
          ]}
        </Columns>
      </Row>
    )
  }

  const tabs = [
    ['Demographics'],
    ['Reports'],
    ['OutputReports'],
    ['Administration'],
    ['Workflows']
  ]

  return (
    <Form>
      <Row>
        <Col sm='6' md='3' lg='2'>
          <Label><Resx name='JobPosition.OrganizationalUnitType' /></Label>
        </Col>
        <Col sm='6' md='9' lg='10'>
          {localizedName(jobPositionPermissions, 'organizationalUnitType')}
        </Col>
      </Row>
      <Row>
        <Col sm='6' md='3' lg='2'>
          <Label><Resx name='JobPosition.OrganizationalUnit' /></Label>
        </Col>
        <Col sm='6' md='9' lg='10'>
          {localizedName(jobPositionPermissions, 'organizationalUnit')}
        </Col>
      </Row>
      <Row>
        <Col sm='6' md='3' lg='2'>
          <Label><Resx name='JobPosition.JobPositionTitle' /></Label>
        </Col>
        <Col sm='6' md='9' lg='10'>
          {localizedName(jobPositionPermissions, 'jobPosition')}
        </Col>
      </Row>
      <Row>
        <Col>
          <TabNavItems
            activeTabId={activeTab}
            onClick={setActiveTab}
            items={[
              { id: '1', text: 'Permissions.Demographics' },
              { id: '2', text: 'Permissions.Reports' },
              { id: '3', text: 'Permissions.OutputReports' },
              { id: '4', text: 'Permissions.Administration' },
              { id: '5', text: 'Permissions.Workflows' },
              { id: '6', text: 'Permissions.Others' }
            ]} />
          <TabContent activeTab={activeTab} renderActiveOnly>
            <TabPane tabId='1'>
              <Row>
                <Col xl={3} lg={4} md={12}>
                  {jobPositionPermissions.allPermissions.filter(x => tabs[0].find(y => y === x.key))
                    .map(group => {
                      return <PermissionGroup key={group.key}
                        group={group}
                        granted={granted}
                        onChange={handleChange}
                        disabledPermissions={disabledPermissions} />
                    })}
                </Col>
                <Col xl={9} lg={8} md={12}>
                  <DemographicsDynamicPermissions
                    key='institution-permissions'
                    institutionPermissions={institutionPermissions || []}
                    onChange={setInstitutionPermissions}
                  />
                </Col>
              </Row>
            </TabPane>
            <TabPane tabId='2'>
              <Row>
                <Col xl={3} lg={4} md={12}>
                  {jobPositionPermissions.allPermissions.filter(x => tabs[1].find(y => y === x.key))
                    .map(group => <PermissionGroup key={group.key} group={group} granted={granted} onChange={handleChange} />)}
                </Col>
                <Col xl={9} lg={8} md={12}>
                  <InputReportPermissions
                    key='input-report-permissions'
                    reportGroupPermissions={reportGroupPermissions || []}
                    jobPositionAssignmentId={jobAssignmentId}
                    onChange={setReportGroupPermissions}
                  />
                </Col>
              </Row>
            </TabPane>
            <TabPane tabId='3'>
              <Row>
                <Col xl={3} lg={4} md={12}>
                  {jobPositionPermissions.allPermissions.filter(x => tabs[2].find(y => y === x.key))
                    .map(group => <PermissionGroup key={group.key} group={group} granted={granted} onChange={handleChange} />)}
                </Col>
                <Col xl={9} lg={8} md={12}>
                  <OutputReportDefinitionPermissions
                    key='input-report-permissions'
                    outputReportDefinitionPermissions={outputReportDefinitionPermissions || []}
                    jobPositionAssignmentId={jobAssignmentId}
                    onChange={setOutputReportDefinitionPermissions}
                  />
                </Col>
              </Row>
            </TabPane>
            <TabPane tabId='4'>
              {renderGroups(x => tabs[3].find(y => y === x.key))}
            </TabPane>
            <TabPane tabId='5'>
              {renderGroups(x => tabs[4].find(y => y === x.key))}
            </TabPane>
            <TabPane tabId='6'>
              {renderGroups(x => !tabs.find(t => t.find(y => y === x.key)))}
            </TabPane>
          </TabContent>
        </Col>
      </Row>
      <Row>
        <Col>
          <hr />
        </Col>
      </Row>
      <Row>
        <Col>
          <Button color='success' onClick={handleSave} disabled={!pendingChanges}>
            <Icon name='save' />
            <span><Resx name='Common.Save' /></span>
          </Button>
        </Col>
      </Row>
    </Form>
  )
})

function PermissionGroup ({ group, granted, onChange, disabledPermissions }) {
  return (
    <WhiteBox header={`Permissions.${group.key}`} transparent headerClass='no-uppercase'>
      {group.value.map(x => (
        <PermissionCheckbox
          key={x}
          name={x}
          groupName={group.key}
          checked={granted.indexOf(x) >= 0}
          onChange={checked => onChange(x, checked)}
          disabled={(disabledPermissions || []).includes(x)}
        />
      ))}
    </WhiteBox>
  )
}

function PermissionCheckbox ({ name, groupName, checked, onChange, disabled }) {
  const { localize } = useLocalization()
  const normalizedName = name.replace(/[.-]/g, '')
  return (
    <CheckBox
      id={`permission-checkbox-${name}`}
      label={localize(`InternalPermissions.${normalizedName}`)}
      checked={checked}
      onChange={onChange}
      disabled={disabled}
    />
  )
}

export default JobPositionPermissionsForm



// WEBPACK FOOTER //
// ./src/internal-app/pages/jobPositionPermissions/form.js